<template>
    <div class="page products-page">
        <section class="restricted-width">
            <v-container>
                <v-row>
                    <div class="section-title">
                        <h1>{{ terms.offers }}</h1>
                    </div>
                </v-row>


                <!-- Loading State -->
                <v-row v-if="loading" justify="center" class="my-8">
                    <v-progress-circular indeterminate color="#ffc451" size="64"></v-progress-circular>
                </v-row>

                <!-- Products List with FLIP Animation -->
                <transition-group v-else :name="route.query.product ? '' : 'product-list'" tag="div"
                    class="products-list">
                    <v-card v-for="product in sortedProducts" :key="product.id" :id="`product-card-${product.id}`"
                        :class="['product-item-card', 'mb-6', `product-card-${product.id}`]" elevation="3" rounded="lg">
                        <v-card-title
                            class="product-main-title pa-3 pa-sm-4 pb-2 d-flex align-start justify-space-between ga-2">
                            <h3 class="text-h6 text-md-h5 font-weight-bold text-dark mb-0 flex-grow-1 mr-2 text-wrap">{{
                                product.header }}</h3>
                            <button class="btn-facebook-pill flex-shrink-0" @click="shareProduct(product)"
                                :title="`${terms.share} Facebook`">
                                <i class="fab fa-facebook mr-0 mr-sm-1"></i>
                                <span class="d-none d-sm-inline">{{ terms.share }}</span>
                            </button>
                        </v-card-title>

                        <v-divider></v-divider>

                        <v-card-text class="pa-4 product-card-text-container">
                            <v-row align="start" class="mobile-product-top-row">
                                <!-- Left: Product Image -->
                                <v-col cols="12" md="5" lg="4" class="text-center mobile-image-col">
                                    <div class="image-wrapper pa-2">
                                        <v-img :src="product.imageSrc" :alt="product.header" class="product-img rounded"
                                            max-height="300" min-height="200" aspect-ratio="1.3" contain></v-img>
                                    </div>
                                </v-col>

                                <!-- Right: Price & Purchase Box -->
                                <v-col cols="12" md="7" lg="8" class="mobile-info-col">
                                    <div class="d-flex flex-column justify-space-between h-100">
                                        <div class="top-info-row mb-3">
                                            <!-- Price -->
                                            <div class="price-display mb-3 mb-sm-4">
                                                <span class="price-highlight">{{ formatNumber(product.priceEuro)
                                                    }}&nbsp;{{ terms.currency_euro }}</span>
                                                <span class="price-separator mx-2"> / </span>
                                                <span class="price-highlight">{{ formatNumber(product.priceLeva)
                                                    }}&nbsp;{{ terms.currency_leva }}</span>
                                            </div>
                                            <!-- Code, Specs & Wishlist -->
                                            <div
                                                class="d-flex align-center flex-wrap text-caption text-grey-darken-1 ga-3 mb-2">
                                                <span
                                                    class="v-chip v-chip--density-compact v-chip--size-small color-chip pa-1 px-2 border rounded bg-grey-lighten-4">
                                                    <strong>{{ terms.product_code }}:&nbsp;</strong>{{ product.code }}
                                                </span>
                                                <span v-if="product.dimensions"
                                                    class="v-chip v-chip--density-compact v-chip--size-small color-chip pa-1 px-2 border rounded bg-grey-lighten-4">
                                                    <strong>{{ terms.dimensions || 'Dimensions' }}:&nbsp;</strong>{{ (product.dimensions ||
                                                        '').replace(/\*/g, 'x') }} cm
                                                </span>
                                                <span v-if="product.packaging"
                                                    class="v-chip v-chip--density-compact v-chip--size-small color-chip pa-1 px-2 border rounded bg-grey-lighten-4">
                                                    <strong>{{ terms.packaging || 'Packaging' }}:&nbsp;</strong>{{ product.packaging }}
                                                </span>
                                                <span v-if="product.weight"
                                                    class="v-chip v-chip--density-compact v-chip--size-small color-chip pa-1 px-2 border rounded bg-grey-lighten-4">
                                                    <strong>{{ terms.weight || 'Weight' }}:&nbsp;</strong>{{ product.weight }} kg
                                                </span>
                                                <button class="favorite-btn ml-auto"
                                                    @click="toggleFavorite(product.id)">
                                                    <v-icon size="small"
                                                        :color="favorites[product.id] ? 'red' : 'grey'">
                                                        {{ favorites[product.id] ? 'mdi-heart' : 'mdi-heart-outline' }}
                                                    </v-icon>
                                                    <span>{{ terms.add_to_favorites }}</span>
                                                </button>
                                            </div>
                                        </div>

                                        <!-- Purchase Options Card (Ref Image Style) -->
                                        <v-card class="purchase-options-box pa-3 mb-4" variant="outlined"
                                            color="grey-lighten-2">
                                            <div class="box-header d-flex align-center justify-space-between mb-2">
                                                <span
                                                    class="font-weight-bold text-subtitle-2 text-uppercase text-grey-darken-3">
                                                    {{ terms.purchase_options }}
                                                </span>
                                                <button type="button" class="cart-link-btn"
                                                    @click.prevent.stop="goToCart" :title="terms.view_cart">
                                                    <v-icon color="warning">mdi-cart-outline</v-icon>
                                                </button>
                                            </div>

                                            <div class="purchase-controls-row d-flex align-center flex-wrap ga-3">
                                                <!-- Requirement 1: Show 1st add to cart button when no items added (getCartQty === 0). Stepper + - hidden. -->
                                                <template v-if="getCartQty(product.id) === 0">
                                                    <button type="button"
                                                        class="add-to-cart-gold-btn py-2 px-3 px-sm-5 rounded font-weight-bold d-inline-flex align-center"
                                                        @click.prevent.stop="onInitialAddToCart(product, $event)">
                                                        <v-icon class="mr-1">mdi-cart</v-icon>
                                                        <span class="d-none d-sm-inline">{{ terms.add_to_cart || 'Add to cart' }}</span>
                                                        <span class="d-inline d-sm-none">{{ terms.buy_short || terms.menu_buy || 'Buy' }}</span>
                                                    </button>
                                                </template>

                                                <!-- Requirement 3: Delete button inline on exact same line with + - buttons -->
                                                <template v-else>
                                                    <div class="d-flex align-center flex-nowrap ga-2">
                                                        <button type="button" class="btn-remove-cart"
                                                            @click.prevent.stop="onRemoveFromCart(product, $event)"
                                                            :title="terms.remove_from_cart">
                                                            <v-icon size="small">mdi-trash-can-outline</v-icon>
                                                        </button>
                                                        <div class="quantity-stepper d-inline-flex align-center">
                                                            <button type="button" class="step-btn"
                                                                @click.prevent.stop="onDecrementCart(product, $event)"
                                                                :disabled="getCartQty(product.id) <= 1">-</button>
                                                            <input type="number" class="qty-input"
                                                                :value="getCartQty(product.id)"
                                                                @input="e => onInputCartQty(product, e.target.value)"
                                                                min="1" max="100" />
                                                            <button type="button" class="step-btn"
                                                                @click.prevent.stop="onIncrementCart(product, $event)">+</button>
                                                        </div>
                                                    </div>
                                                </template>
                                            </div>
                                        </v-card>
                                    </div>
                                </v-col>
                            </v-row>

                            <!-- Compact Description, Advantages & Applications -->
                            <div class="details-accordion-section mt-3 pt-2 border-top">
                                <p class="description-text mb-2">{{ product.description }}</p>

                                <!-- Details Toggle Button for Screens < 960px -->
                                <div v-if="!$vuetify.display.mdAndUp && ((product.advantages && product.advantages.length) || (product.applications && product.applications.length))"
                                    class="text-center my-2">
                                    <button type="button" class="product-details-toggle-btn"
                                        @click="toggleDetails(product.id)">
                                        <span>
                                            {{ expandedDetails[product.id] ? (terms.hide_details || 'Hide details') : (terms.more_details || 'More details') }}
                                        </span>
                                        <v-icon size="small" class="ml-1">
                                            {{ expandedDetails[product.id] ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                                        </v-icon>
                                    </button>
                                </div>

                                <v-row
                                    v-if="($vuetify.display.mdAndUp || expandedDetails[product.id]) && ((product.advantages && product.advantages.length) || (product.applications && product.applications.length))"
                                    class="mt-1">
                                    <v-col v-if="product.advantages && product.advantages.length" cols="12" md="6"
                                        class="py-1">
                                        <h4 class="text-subtitle-2 font-weight-bold price-highlight mb-1">
                                            {{ terms.advantages }}
                                        </h4>
                                        <ul class="bullet-list pl-4">
                                            <li v-for="(adv, idx) in product.advantages" :key="idx">{{ adv }}</li>
                                        </ul>
                                    </v-col>
                                    <v-col v-if="product.applications && product.applications.length" cols="12" md="6"
                                        class="py-1">
                                        <h4 class="text-subtitle-2 font-weight-bold price-highlight mb-1">
                                            {{ terms.applications }}
                                        </h4>
                                        <ul class="bullet-list pl-4">
                                            <li v-for="(app, idx) in product.applications" :key="idx">{{ app }}</li>
                                        </ul>
                                    </v-col>
                                </v-row>
                            </div>
                        </v-card-text>
                    </v-card>
                </transition-group>
            </v-container>
        </section>

        <!-- Toast Notification for Add to Cart -->
        <v-snackbar v-model="snackbar" :timeout="1000" color="success" location="top"
            class="custom-add-to-cart-snackbar" :style="{ '--widget-bottom-offset': `${widgetBottomOffset}px` }" density="compact">
            <div class="d-flex align-center snackbar-content-wrapper ga-2">
                <v-icon size="16">mdi-check-circle</v-icon>
                <span class="snackbar-text-content font-weight-medium">{{ snackbarText }}</span>
            </div>
        </v-snackbar>
    </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, inject, nextTick, watch } from 'vue';
import { useStore } from 'vuex';
import { useRouter, useRoute } from 'vue-router';
import _ from 'lodash';

const axios = inject('axios');
const store = useStore();
const router = useRouter();
const route = useRoute();

const terms = computed(() => store.state.terms);
const isAlreadyLoaded = computed(() => {
    return Boolean(
        store.state.products &&
        store.state.products.length > 0 &&
        store.state.productsLanguage === store.state.languageCode
    );
});

const initialProducts = (store.state.products && store.state.productsLanguage === store.state.languageCode) ? store.state.products : [];
const loading = ref(initialProducts.length === 0);
const products = ref(initialProducts);
const selectedQuantities = reactive({});

if (initialProducts.length > 0) {
    initialProducts.forEach(p => {
        selectedQuantities[p.id] = 1;
    });
}
const favorites = computed(() => store.state.favorites);
const isFavorite = (id) => {
    const favs = favorites.value;
    if (!favs) return false;
    if (Array.isArray(favs)) return favs.includes(id);
    return Boolean(favs[id]);
};

const getFavTime = (id) => {
    const favs = favorites.value;
    if (!favs) return 0;
    if (Array.isArray(favs)) {
        const idx = favs.indexOf(id);
        return idx !== -1 ? (favs.length - idx) : 0;
    }
    const val = favs[id];
    if (typeof val === 'number') return val;
    if (val === true) return 1; // Fallback for legacy stored booleans
    return 0;
};

const getProductTypeRank = (p) => {
    const id = Number(p.id);
    const code = (p.code || '').toUpperCase();

    // 1. Tape dispenser
    if (id === 1 || code === 'TM1') return 1;

    // 2. Band (one or many)
    if (id === 2 || id === 12 || code === 'L1' || code === 'L10' || code.startsWith('L')) return 2;

    // 4. Bundle (tape + dispenser)
    if (id === 4 || id === 5 || id === 10 || id === 11 || id === 14 ||
        code.includes('PR') || code.includes('MA') ||
        (p.header && (p.header.toLowerCase().includes('dispenser +') || p.header.toLowerCase().includes('\u043c\u0430\u0448\u0438\u043d\u043a\u0430 +')))) {
        return 4;
    }

    // 3. Tape (one or many)
    return 3;
};

const sortedProducts = computed(() => {
    if (!products.value || !products.value.length) return [];
    return [...products.value].sort((a, b) => {
        const timeA = getFavTime(a.id);
        const timeB = getFavTime(b.id);
        if (timeA !== timeB) {
            return timeB - timeA; // Most recently favorited item on top!
        }

        const rankA = getProductTypeRank(a);
        const rankB = getProductTypeRank(b);
        if (rankA !== rankB) {
            return rankA - rankB;
        }

        const priceA = Number(a.priceEuro || a.price || 0);
        const priceB = Number(b.priceEuro || b.price || 0);
        if (priceA !== priceB) {
            return priceA - priceB;
        }

        return a.id - b.id;
    });
});
const snackbar = ref(false);
const snackbarText = ref('');
const widgetBottomOffset = ref(130);

const showSnackbar = (text) => {
    snackbarText.value = text;
    if (typeof window !== 'undefined') {
        const widgetEl = document.querySelector('.free-delivery-widget-wrapper');
        if (widgetEl) {
            const rect = widgetEl.getBoundingClientRect();
            widgetBottomOffset.value = Math.max(Math.round(rect.bottom + 8), 65);
        } else {
            const headerHeight = window.innerWidth < 600 ? 52 : (window.innerWidth < 960 ? 60 : 72);
            widgetBottomOffset.value = headerHeight + 12;
        }
    }
    snackbar.value = true;
};
const expandedDetails = reactive({});

const toggleDetails = (productId) => {
    expandedDetails[productId] = !expandedDetails[productId];
};

const formatNumber = (val) => {
    if (val === undefined || val === null) return '0.00';
    return Number(val).toFixed(2).replace('.', ',');
};

const fetchProducts = async () => {
    if (isAlreadyLoaded.value) {
        products.value = store.state.products;
        products.value.forEach(p => {
            if (!selectedQuantities[p.id]) {
                selectedQuantities[p.id] = 1;
            }
        });
        loading.value = false;
        return;
    }

    loading.value = true;
    try {
        const data = await store.dispatch('fetchProducts', { axios });
        products.value = data || [];
        // Initialize quantities
        products.value.forEach(p => {
            if (!selectedQuantities[p.id]) {
                selectedQuantities[p.id] = 1;
            }
        });
    } catch (err) {
        console.error("Failed to load products:", err);
    } finally {
        loading.value = false;
    }
};

const cartItems = computed(() => store.state.cart);
const cartItemCount = computed(() => store.getters.cartItemCount);

const getCartQty = (productId) => {
    const item = cartItems.value.find(i => i.id === productId);
    return item ? item.quantity : 0;
};

const toggleFavorite = (id) => {
    const isAdding = !isFavorite(id);
    store.commit('toggleFavorite', id);

    if (isAdding) {
        nextTick(() => {
            setTimeout(() => {
                const targetCard = document.getElementById(`product-card-${id}`) || document.querySelector(`.product-card-${id}`);
                if (targetCard) {
                    scrollToCardAtTop(id);

                    setTimeout(() => {
                        const finalCard = document.getElementById(`product-card-${id}`) || document.querySelector(`.product-card-${id}`);
                        if (finalCard) {
                            finalCard.classList.remove('card-shake-animate');
                            void finalCard.offsetWidth; // Force animation restart
                            finalCard.classList.add('card-shake-animate');

                            setTimeout(() => {
                                finalCard.classList.remove('card-shake-animate');
                            }, 1000);
                        }
                    }, 500);
                }
            }, 750);
        });
    }
};

const createTapeDispenserParticle = () => {
    const flyer = document.createElement('div');
    flyer.className = 'fly-cart-particle';
    flyer.style.backgroundImage = "url('/img/tape_dispenser_new.jpg')";
    flyer.style.backgroundSize = 'contain';
    flyer.style.backgroundRepeat = 'no-repeat';
    flyer.style.backgroundPosition = 'center';
    flyer.style.backgroundColor = '#141414';
    flyer.style.border = '2px solid #ffc451';
    flyer.style.borderRadius = '50%';
    flyer.style.boxShadow = '0 8px 24px rgba(255, 196, 81, 0.85)';
    flyer.style.position = 'fixed';
    flyer.style.zIndex = '999999';
    flyer.style.pointerEvents = 'none';
    flyer.style.transition = 'all 1.1s cubic-bezier(0.22, 1, 0.36, 1)';
    return flyer;
};

const flyToCartAnimation = (startEvent) => {
    const cartHeader = document.querySelector('#order-cart-icon-btn') || document.querySelector('#header-cart-btn') || document.querySelector('.cart-btn');
    if (!cartHeader) return;

    let startX = window.innerWidth / 2;
    let startY = window.innerHeight / 2;
    if (startEvent && startEvent.clientX !== undefined) {
        startX = startEvent.clientX;
        startY = startEvent.clientY;
    } else if (startEvent && startEvent.target) {
        const rect = startEvent.target.getBoundingClientRect();
        startX = rect.left + rect.width / 2;
        startY = rect.top + rect.height / 2;
    }

    const cartRect = cartHeader.getBoundingClientRect();
    const targetX = cartRect.left + cartRect.width / 2;
    const targetY = cartRect.top + cartRect.height / 2;

    const flyer = createTapeDispenserParticle();
    flyer.style.left = `${startX - 28}px`;
    flyer.style.top = `${startY - 28}px`;
    flyer.style.width = '56px';
    flyer.style.height = '56px';

    document.body.appendChild(flyer);

    requestAnimationFrame(() => {
        flyer.style.left = `${targetX - 16}px`;
        flyer.style.top = `${targetY - 16}px`;
        flyer.style.width = '32px';
        flyer.style.height = '32px';
        flyer.style.opacity = '0.2';
        flyer.style.transform = 'scale(0.5) rotate(360deg)';
    });

    setTimeout(() => {
        if (flyer.parentNode) {
            flyer.parentNode.removeChild(flyer);
        }
        cartHeader.classList.add('cart-bump-animate');
        setTimeout(() => {
            cartHeader.classList.remove('cart-bump-animate');
        }, 400);
    }, 1100);
};

const flyFromCartAnimation = (targetEvent) => {
    const cartHeader = document.querySelector('#order-cart-icon-btn') || document.querySelector('#header-cart-btn') || document.querySelector('.cart-btn');
    if (!cartHeader) return;

    const cartRect = cartHeader.getBoundingClientRect();
    const startX = cartRect.left + cartRect.width / 2;
    const startY = cartRect.top + cartRect.height / 2;

    let targetX = window.innerWidth / 2;
    let targetY = window.innerHeight / 2;
    let targetEl = null;

    if (targetEvent && targetEvent.clientX !== undefined) {
        targetX = targetEvent.clientX;
        targetY = targetEvent.clientY;
        targetEl = targetEvent.target ? targetEvent.target.closest('.product-item-card') : null;
    } else if (targetEvent && targetEvent.target) {
        const rect = targetEvent.target.getBoundingClientRect();
        targetX = rect.left + rect.width / 2;
        targetY = rect.top + rect.height / 2;
        targetEl = targetEvent.target.closest('.product-item-card');
    }

    const flyer = createTapeDispenserParticle();
    flyer.style.left = `${startX - 16}px`;
    flyer.style.top = `${startY - 16}px`;
    flyer.style.width = '32px';
    flyer.style.height = '32px';

    document.body.appendChild(flyer);

    cartHeader.classList.add('cart-bump-animate');
    setTimeout(() => {
        cartHeader.classList.remove('cart-bump-animate');
    }, 400);

    requestAnimationFrame(() => {
        flyer.style.left = `${targetX - 28}px`;
        flyer.style.top = `${targetY - 28}px`;
        flyer.style.width = '56px';
        flyer.style.height = '56px';
        flyer.style.opacity = '0.2';
        flyer.style.transform = 'scale(0.5) rotate(-360deg)';
    });

    setTimeout(() => {
        if (flyer.parentNode) {
            flyer.parentNode.removeChild(flyer);
        }
        if (targetEl) {
            targetEl.classList.add('card-shake-animate');
            setTimeout(() => {
                targetEl.classList.remove('card-shake-animate');
            }, 400);
        }
    }, 1100);
};

const onInitialAddToCart = (product, event) => {
    store.commit('addToCart', { product, quantity: 1 });
    flyToCartAnimation(event, product.imageSrc);
    showSnackbar(terms.value?.added || 'Added');
};

const onIncrementCart = (product, event) => {
    const currentQty = getCartQty(product.id);
    if (currentQty < 100) {
        const newQty = currentQty + 1;
        store.commit('addToCart', { product, quantity: newQty });
        flyToCartAnimation(event, product.imageSrc);
        showSnackbar(terms.value?.updated || 'Updated');
    }
};

const onDecrementCart = (product, event) => {
    const currentQty = getCartQty(product.id);
    if (currentQty > 1) {
        const newQty = currentQty - 1;
        store.commit('addToCart', { product, quantity: newQty });
        flyFromCartAnimation(event, false);
    }
};

const onInputCartQty = (product, val) => {
    const qty = parseInt(val, 10);
    if (isNaN(qty) || qty <= 0) {
        store.commit('removeFromCart', product.id);
    } else {
        const targetQty = Math.min(Math.max(qty, 1), 100);
        store.commit('addToCart', { product, quantity: targetQty });
    }
};

const onRemoveFromCart = (product, event) => {
    store.commit('removeFromCart', product.id);
    flyFromCartAnimation(event, true);
    showSnackbar(terms.value?.removed || 'Removed');
};

const goToCart = () => {
    router.push('/order');
};

const copyTextToClipboard = async (text) => {
    if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
    } else {
        const textArea = document.createElement("textarea");
        textArea.value = text;
        textArea.style.position = "fixed";
        textArea.style.left = "-999999px";
        textArea.style.top = "-999999px";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        try {
            document.execCommand('copy');
        } catch (err) {
            console.error('Fallback copy failed', err);
        }
        document.body.removeChild(textArea);
    }
};

const shareProduct = async (product) => {
    const productTitle = product.header;
    const productPrice = `${formatNumber(product.priceEuro)} € / ${formatNumber(product.priceLeva)} ${terms.value?.currency_leva || 'BGN'}`;
    const shareUrl = `${window.location.origin}/offers`;
    const dimText = product.dimensions ? `${terms.value?.dimensions || 'Dimensions'}: ${(product.dimensions || '').replace(/\*/g, 'x')} cm` : '';
    const fullShareText = `AVEM: ${productTitle}\n${terms.value?.price || 'Price'}: ${productPrice}${dimText ? `\n${dimText}` : ''}\n${shareUrl}`;

    // 1. Try native Web Share API (Mobile & Modern Browsers)
    if (navigator.share) {
        try {
            await navigator.share({
                title: productTitle,
                text: fullShareText,
                url: shareUrl
            });
            showSnackbar(`${productTitle} -> ${terms.value?.shared_successfully || 'Shared successfully!'}`);
            return;
        } catch (err) {
            if (err.name !== 'AbortError') {
                console.warn('Web Share API error:', err);
            } else {
                return; // User canceled share dialog
            }
        }
    }

    // 2. Fallback for Web Facebook Dialog: Copy rich text payload to clipboard
    try {
        await copyTextToClipboard(fullShareText);
    } catch (e) {
        console.warn('Clipboard copy failed:', e);
    }

    // 3. Open Facebook Share Dialog with rich quote parameter
    const fbShareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(fullShareText)}`;
    window.open(fbShareUrl, '_blank', 'width=600,height=500');

    showSnackbar((terms.value?.product_details_copied || 'Product details for "$TITLE$" copied! Press Ctrl+V to paste into your post.').replace('$TITLE$', productTitle));
};

const scrollToCardAtTop = (targetId) => {
    const targetCard = document.getElementById(`product-card-${targetId}`) || document.querySelector(`.product-card-${targetId}`);
    if (!targetCard) return;
    const headerOffset = window.innerWidth < 960 ? 80 : 98;

    const rect = targetCard.getBoundingClientRect();
    const topPos = window.pageYOffset + rect.top - headerOffset;
    window.scrollTo({
        top: Math.max(0, topPos),
        behavior: 'smooth'
    });
};

const triggerProductScroll = (targetId) => {
    if (!targetId) return;

    let attempts = 0;
    const maxAttempts = 30; // Retry for up to 1.5s if DOM node is still mounting

    const findAndScroll = () => {
        const targetCard = document.getElementById(`product-card-${targetId}`) || document.querySelector(`.product-card-${targetId}`);
        if (targetCard) {
            // 1. Highlight card immediately
            targetCard.classList.add('card-target-highlight');

            // 2. Smooth scroll to absolute top position of card
            scrollToCardAtTop(targetId);

            // 3. Trigger shake animation after scroll initiation
            setTimeout(() => {
                const finalCard = document.getElementById(`product-card-${targetId}`) || document.querySelector(`.product-card-${targetId}`);
                if (finalCard) {
                    finalCard.classList.remove('card-target-highlight');
                    finalCard.classList.remove('card-shake-animate');
                    void finalCard.offsetWidth; // Force animation restart
                    finalCard.classList.add('card-shake-animate');

                    setTimeout(() => {
                        finalCard.classList.remove('card-shake-animate');
                    }, 1000);
                }
            }, 800);
        } else if (attempts < maxAttempts) {
            attempts++;
            setTimeout(findAndScroll, 50);
        }
    };

    nextTick(() => {
        setTimeout(findAndScroll, 100);
    });
};

watch(() => route.query.product, (newId) => {
    if (newId) {
        triggerProductScroll(newId);
    }
});

onMounted(async () => {
    await fetchProducts();
    const targetId = route.query.product || (route.hash ? route.hash.replace('#product-', '') : null);
    if (targetId) {
        triggerProductScroll(targetId);
    }
});
</script>

<style scoped>
.products-page {
    background-color: #f8f9fa;
    min-height: 80vh;
    padding-bottom: 60px;
}

.products-page section:first-of-type {
    padding-top: 0px !important;
}

/* Slow smooth FLIP animation for favoriting items to top */
.products-list>.product-list-move {
    transition: transform 0.75s cubic-bezier(0.25, 1, 0.35, 1) !important;
}

.products-list>.product-list-enter-active,
.products-list>.product-list-leave-active {
    transition: all 0.75s cubic-bezier(0.25, 1, 0.35, 1) !important;
}

.products-list>.product-list-enter-from,
.products-list>.product-list-leave-to {
    opacity: 0;
    transform: scale(0.96) translateY(20px);
}

@property --border-angle {
    syntax: "<angle>";
    inherits: false;
    initial-value: 0deg;
}

@keyframes spinBorder {
    to {
        --border-angle: 360deg;
    }
}

.product-item-card {
    position: relative;
    border-radius: 20px !important;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.06), 0 0 12px rgba(255, 196, 81, 0.15) !important;
    transition: box-shadow 0.35s ease, border-color 0.35s ease, transform 0.35s ease;
    background: #ffffff !important;
    scroll-margin-top: 85px !important;
}

@media (max-width: 959px) {
    .product-item-card {
        scroll-margin-top: 70px !important;
    }
}

.product-item-card::before {
    content: "";
    position: absolute;
    inset: -3px;
    border-radius: 22px;
    padding: 3px;
    background: conic-gradient(from var(--border-angle),
            transparent 0deg,
            rgba(255, 196, 81, 0.4) 60deg,
            #ffc451 120deg,
            #ffffff 140deg,
            #ffc451 160deg,
            rgba(255, 196, 81, 0.4) 220deg,
            transparent 280deg);
    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    opacity: 0;
    transition: opacity 0.4s ease;
    pointer-events: none;
    z-index: 10;
    animation: spinBorder 3.5s linear infinite;
}

.product-item-card:hover::before {
    opacity: 1;
}

.product-item-card:hover {
    border-color: #ffc451 !important;
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.12), 0 0 28px rgba(255, 196, 81, 0.5) !important;
    transform: translateY(-3px);
}

.card-target-highlight {
    border-color: #ffc451 !important;
    box-shadow: 0 12px 35px rgba(0, 0, 0, 0.18), 0 0 30px rgba(255, 196, 81, 0.7) !important;
    z-index: 90 !important;
    transition: all 0.3s ease;
}

.card-target-highlight::before {
    opacity: 1 !important;
}

@keyframes cardLandingPop {
    0% {
        transform: scale(1) translateY(0);
        box-shadow: 0 0 0 0 rgba(255, 196, 81, 0.9);
    }

    20% {
        transform: scale(1.05) translateY(-8px);
        box-shadow: 0 20px 50px rgba(0, 0, 0, 0.25), 0 0 45px rgba(255, 196, 81, 0.95);
        border-color: #ffc451 !important;
    }

    35% {
        transform: scale(1.04) translateX(-9px);
        border-color: #ffc451 !important;
    }

    50% {
        transform: scale(1.03) translateX(9px);
        border-color: #ffc451 !important;
    }

    65% {
        transform: scale(1.02) translateX(-5px);
        border-color: #ffc451 !important;
    }

    80% {
        transform: scale(1.01) translateX(5px);
        border-color: #ffc451 !important;
    }

    100% {
        transform: scale(1) translateY(0);
        box-shadow: 0 4px 15px rgba(0, 0, 0, 0.06), 0 0 12px rgba(255, 196, 81, 0.15);
    }
}

.card-shake-animate {
    animation: cardLandingPop 0.85s cubic-bezier(0.25, 1, 0.5, 1) !important;
    border-color: #ffc451 !important;
    z-index: 100 !important;
}

.card-shake-animate::before {
    opacity: 1 !important;
}


.product-main-title {
    white-space: normal !important;
    word-break: break-word;
}

.product-main-title h3 {
    color: #1a252f;
    line-height: 1.3;
}

.image-wrapper {
    background: #ffffff;
    display: flex;
    align-items: center;
    justify-content: center;
}

.product-img {
    object-fit: contain;
}

.price-display {
    font-size: 1.5rem;
    font-weight: 700;
    color: #2c5282;
    /* Clean teal/blue as in screenshot */
}

.price-highlight {
    color: #2b6cb0;
}

.favorite-btn {
    background: none;
    border: none;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 0;
    margin-left: 12px;
}

.purchase-options-box {
    background: #f7fafc;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
}

.quantity-stepper {
    border: 1px solid #cbd5e0;
    border-radius: 4px;
    overflow: hidden;
    background: #ffffff;
}

.step-btn {
    width: 28px;
    height: 32px;
    background: #edf2f7;
    border: none;
    font-weight: bold;
    font-size: 16px;
    cursor: pointer;
    color: #4a5568;
    transition: background 0.2s;
}

.step-btn:hover:not(:disabled) {
    background: #cbd5e0;
}

.qty-input {
    width: 44px;
    height: 32px;
    text-align: center;
    border: none;
    border-left: 1px solid #cbd5e0;
    border-right: 1px solid #cbd5e0;
    font-weight: 600;
    font-size: 14px;
    outline: none;
}

/* Chrome, Safari, Edge, Opera remove arrows */
.qty-input::-webkit-outer-spin-button,
.qty-input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
}

.add-to-cart-gold-btn {
    font-size: 0.95rem;
    padding: 10px 24px;
    width: auto !important;
    max-width: max-content !important;
    min-width: auto !important;
}

@media (max-width: 600px) {
    .products-page {
        padding-bottom: 40px !important;
    }

    .add-to-cart-gold-btn {
        font-size: 0.88rem !important;
        padding: 8px 16px !important;
        white-space: nowrap !important;
        width: auto !important;
        max-width: max-content !important;
        min-width: auto !important;
    }

    .btn-facebook-pill {
        min-width: 32px !important;
        width: 32px !important;
        height: 32px !important;
        min-height: 32px !important;
        padding: 0 !important;
        display: inline-flex !important;
        align-items: center !important;
        justify-content: center !important;
        border-radius: 50% !important;
        font-size: 1rem !important;
    }
}

.border-top {
    border-top: 1px solid #edf2f7;
}

.description-text {
    font-size: 0.92rem;
    line-height: 1.6;
    color: #4a5568;
}

.bullet-list {
    font-size: 0.88rem;
    color: #2d3748;
    line-height: 1.5;
}

.bullet-list li {
    margin-bottom: 4px;
}

.main-heading {
    font-size: 1.1rem;
    color: #718096;
}

.cart-link-btn {
    background: transparent;
    border: none;
    cursor: pointer;
    padding: 2px;
    display: inline-flex;
    align-items: center;
    transition: transform 0.2s ease;
}

.cart-link-btn:hover {
    transform: scale(1.2);
}

.custom-add-to-cart-snackbar {
    top: 0 !important;
    bottom: auto !important;
    z-index: 9999 !important;
}

.custom-add-to-cart-snackbar :deep(.v-snackbar__wrapper) {
    margin-top: var(--widget-bottom-offset, 130px) !important;
    border-radius: 12px;
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.4);
    transition: margin-top 0.2s ease;
}

@media (min-width: 1950px) {
    .custom-add-to-cart-snackbar :deep(.v-snackbar__wrapper) {
        position: fixed !important;
        left: calc(50vw + 600px) !important;
        right: auto !important;
        transform: none !important;
        width: 440px !important;
        max-width: 440px !important;
    }
}

.product-details-toggle-btn {
    background: rgba(240, 240, 240, 0.95);
    color: #2b6cb0;
    border: 1px solid #cbd5e0;
    border-radius: 16px;
    padding: 4px 14px;
    font-size: 0.78rem;
    font-weight: 600;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
}

.free-shipping-banner {
    padding: 10px 16px !important;
}

.free-shipping-banner-text {
    font-size: 0.95rem;
    font-weight: 700;
}

.product-details-toggle-btn:hover {
    background: #2b6cb0;
    color: #ffffff;
}

@media (max-width: 959px) {
    .free-shipping-banner {
        padding: 5px 10px !important;
        margin-bottom: 10px !important;
        border-radius: 8px !important;
        min-height: auto !important;
    }

    .free-shipping-banner :deep(.v-alert__icon) {
        font-size: 18px !important;
        margin-right: 4px !important;
    }

    .free-shipping-banner-text {
        font-size: 0.8rem !important;
        line-height: 1.2 !important;
    }

    .product-item-card {
        margin-bottom: 12px !important;
    }

    .product-main-title {
        padding: 8px 12px !important;
    }

    .product-main-title h3 {
        font-size: 1.02rem !important;
    }

    .btn-facebook-pill {
        padding: 2px 8px !important;
        font-size: 0.72rem !important;
    }

    .product-card-text-container {
        padding: 10px 12px !important;
    }

    /* Mobile / Tablet Side-by-side Top Row (< 960px) */
    .mobile-product-top-row {
        display: flex !important;
        flex-direction: row !important;
        flex-wrap: nowrap !important;
        align-items: flex-start !important;
        margin: 0 !important;
    }

    .mobile-image-col {
        flex: 0 0 130px !important;
        max-width: 130px !important;
        padding: 0 8px 0 0 !important;
    }

    .mobile-image-col .image-wrapper {
        padding: 0 !important;
    }

    .mobile-image-col .product-img {
        max-height: 125px !important;
        width: 125px !important;
        margin: 0 auto !important;
    }

    .mobile-info-col {
        flex: 1 1 auto !important;
        max-width: calc(100% - 130px) !important;
        padding: 0 !important;
    }

    .price-display {
        font-size: 1.2rem !important;
        margin-bottom: 4px !important;
    }

    .color-chip {
        font-size: 0.7rem !important;
        padding: 1px 5px !important;
    }

    .favorite-btn {
        margin-left: 0 !important;
        font-size: 0.72rem !important;
    }

    .purchase-options-box {
        padding: 6px 10px !important;
        margin-bottom: 6px !important;
    }

    .box-header {
        margin-bottom: 4px !important;
    }

    .box-header span {
        font-size: 0.74rem !important;
    }

    .add-to-cart-gold-btn {
        font-size: 0.82rem !important;
        padding: 6px 14px !important;
        width: 100% !important;
        justify-content: center !important;
    }

    .details-accordion-section {
        margin-top: 6px !important;
        padding-top: 6px !important;
    }

    .description-text {
        font-size: 0.82rem !important;
        line-height: 1.38 !important;
        margin-bottom: 4px !important;
    }

    .bullet-list {
        font-size: 0.78rem !important;
        line-height: 1.32 !important;
    }

    .custom-add-to-cart-snackbar {
        top: 0 !important;
        bottom: auto !important;
    }

    .custom-add-to-cart-snackbar :deep(.v-snackbar__wrapper) {
        min-width: 260px !important;
        max-width: calc(100vw - 20px) !important;
        padding: 2px 6px !important;
    }

    .snackbar-text-content {
        font-size: 0.78rem !important;
        line-height: 1.2;
    }

    .cart-btn-action {
        font-size: 0.72rem !important;
        padding: 0 6px !important;
        height: 26px !important;
    }
}
</style>
